import { useState, useCallback } from 'react';

export interface TranslationResult {
  translatedText: string;
  detectedLanguage?: string;
  confidence?: number;
}

export interface TranslationError {
  message: string;
  code?: string;
}

export function useTranslation() {
  const [ isTranslating, setIsTranslating ] = useState(false);
  const [ error, setError ] = useState<TranslationError | null>(null);

  const translateText = useCallback(async (
    text: string,
    targetLanguage: string,
    sourceLanguage?: string
  ): Promise<TranslationResult | null> => {
    if (!text.trim()) {
      return null;
    }

    setIsTranslating(true);
    setError(null);

    try {
      // Use Google Translate API via MyMemory (free API)
      const encodedText = encodeURIComponent(text);
      const langPair = sourceLanguage ? `${sourceLanguage}|${targetLanguage}` : `autodetect|${targetLanguage}`;

      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodedText}&langpair=${langPair}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Translation API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.responseStatus === 200 && data.responseData) {
        return {
          translatedText: data.responseData.translatedText,
          detectedLanguage: data.responseData.detectedLanguage,
          confidence: data.responseData.match
        };
      } else {
        throw new Error(data.responseDetails || 'Translation failed');
      }
    } catch (err) {
      const error: TranslationError = {
        message: err instanceof Error ? err.message : 'Translation service unavailable',
        code: 'TRANSLATION_ERROR'
      };
      setError(error);
      console.error('Translation error:', error);
      return null;
    } finally {
      setIsTranslating(false);
    }
  }, []);

  const detectLanguage = useCallback(async (text: string): Promise<string | null> => {
    if (!text.trim()) {
      return null;
    }

    try {
      const encodedText = encodeURIComponent(text);
      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodedText}&langpair=autodetect|en`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Language detection error: ${response.status}`);
      }

      const data = await response.json();

      if (data.responseStatus === 200 && data.responseData) {
        return data.responseData.detectedLanguage || null;
      }

      return null;
    } catch (err) {
      console.error('Language detection error:', err);
      return null;
    }
  }, []);

  return {
    translateText,
    detectLanguage,
    isTranslating,
    error
  };
}